import { useEffect, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import {
  checkLegacyNativeVersion,
  detectLegacyShellSync,
} from '../../services/LegacyNativeVersionGate'
import UseTranslation from '../../hooks/UseTranslation'
import UpdateRequiredScreen from './UpdateRequiredScreen'

/**
 * TEMPORARY MIGRATION CODE - REMOVE ONCE OLD NATIVE BUILDS HAVE MIGRATED.
 *
 * Blocks startup on native builds that predate the permanent native force-update
 * checker. See services/LegacyNativeVersionGate.js for the why and for the two
 * build thresholds.
 *
 * Renders `children` untouched for everyone else, so browser users never see this
 * and never wait on it: `isNativePlatform()` is synchronous, so the initial state
 * already allows the app through and there is no extra render pass on the web.
 */
const LegacyNativeVersionGate = ({ children }) => {
  const { t } = UseTranslation()
  const [status, setStatus] = useState(() => {
    // Decided synchronously so a shell too old to run this bundle is refused on
    // the very first render, before any of the app's own code executes. The
    // async build-number check below only runs for shells that get past this.
    const legacyShell = detectLegacyShellSync()
    if (legacyShell) return { state: 'blocked', storeUrl: legacyShell.storeUrl }

    return Capacitor.isNativePlatform() ? { state: 'checking' } : { state: 'allowed' }
  })

  useEffect(() => {
    if (status.state !== 'checking') return undefined

    let cancelled = false

    checkLegacyNativeVersion().then(({ outdated, storeUrl }) => {
      if (cancelled) return
      setStatus(outdated ? { state: 'blocked', storeUrl } : { state: 'allowed' })
    })

    return () => {
      cancelled = true
    }
    // Runs once: the native build number cannot change while the app is running.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Native only, and only for the one tick the plugin call takes. The native
  // splash screen is still up at this point, so nothing flashes.
  if (status.state === 'checking') return null

  // Children are not rendered at all rather than covered up, so there is no
  // route, dialog or back-button path to reach the app behind the screen.
  if (status.state === 'blocked') {
    return (
      <UpdateRequiredScreen
        storeUrl={status.storeUrl}
        t={t}
      />
    )
  }

  return children
}

export default LegacyNativeVersionGate
